/**
 * MovementAnalysis — N4 (B-2026-0126)
 *
 * Renders deterministic movement-analysis observations from
 * `metadata.deterministic_observations`.
 *
 * Design contracts (from TRACE_NARRATIVE_PLAN.md):
 *  - `deterministic_sentence` is the ONLY user-facing text from each record.
 *    Internal numeric fields (sigma, raw_dev, sigma_bands, ratio, etc.) MUST NOT
 *    be rendered.
 *  - Faults (is_positive=false) appear first, sorted mild → strong by
 *    magnitude_band. Positives (is_positive=true) follow, subtly accented.
 *  - Plausibility-flagged items (is_plausibility_flagged=true) are hidden from
 *    the main list; they render under a separate muted "tracking uncertainty"
 *    disclosure if any are present.
 *  - Observations are grouped by phase_name.
 *  - When `llm_explanation` is absent/null but observations exist, a short
 *    fallback note replaces the coaching section header.
 *  - When `deterministic_observations` is absent or empty the component returns
 *    null (caller must not render a section wrapper either).
 */

import { useState } from 'react';
import { ChevronDownIcon, FingerPrintIcon } from '@heroicons/react/24/outline';

// ─── Data contract ────────────────────────────────────────────────────────────

export type MagnitudeBand = 'silent' | 'mild' | 'moderate' | 'strong' | 'implausible';

export interface DeterministicObservation {
  /** Phase name (e.g. "Preparation", "Execution") */
  phase_name: string;
  /** Human-readable physical body part name (already mirror-corrected by backend) */
  physical_landmark_name: string;
  /** Body region label (e.g. "Left Leg") */
  body_region: string;
  /** Measurement channel */
  channel: 'path' | 'range';
  /**
   * THE display text — already layperson-ready and mirror-corrected.
   * This is the ONLY field that should be rendered to the user.
   */
  deterministic_sentence: string;
  /** Severity band — used for ordering/styling only, never rendered as text */
  magnitude_band: MagnitudeBand;
  /** True when this is a strength/positive observation */
  is_positive: boolean;
  /** True when the observation may be a tracking artifact, not a real fault */
  is_plausibility_flagged: boolean;
  // --- Internal numeric fields — MUST NOT be rendered ---
  raw_dev?: number;
  sigma?: number;
  sigma_bands?: number;
  ratio?: number;
  template_diameter?: number;
  athlete_diameter?: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Ordering weight for magnitude bands — stronger faults first. */
const BAND_ORDER: Record<MagnitudeBand, number> = {
  implausible: 0, // Not shown in main list (plausibility-flagged), but keep order consistent
  strong: 1,
  moderate: 2,
  mild: 3,
  silent: 4,
};

/** Sort faults: stronger magnitude_bands first. */
function sortFaults(obs: DeterministicObservation[]): DeterministicObservation[] {
  return [...obs].sort(
    (a, b) => (BAND_ORDER[a.magnitude_band] ?? 99) - (BAND_ORDER[b.magnitude_band] ?? 99)
  );
}

// ─── Single observation row ───────────────────────────────────────────────────

interface ObservationRowProps {
  observation: DeterministicObservation;
}

function ObservationRow({ observation }: ObservationRowProps) {
  const { deterministic_sentence, is_positive } = observation;

  if (is_positive) {
    return (
      <li
        data-testid="observation-positive"
        className="flex items-start gap-2.5 text-sm leading-relaxed"
      >
        {/* Subtle green accent dot */}
        <span
          aria-hidden="true"
          className="mt-1.5 h-2 w-2 rounded-full flex-shrink-0"
          style={{ backgroundColor: 'var(--color-success, #22c55e)', opacity: 0.75 }}
        />
        <span
          style={{ color: 'var(--color-text-secondary)' }}
          data-testid="observation-sentence"
        >
          {deterministic_sentence}
        </span>
      </li>
    );
  }

  return (
    <li
      data-testid="observation-fault"
      className="flex items-start gap-2.5 text-sm leading-relaxed"
    >
      {/* Neutral bullet for faults */}
      <span
        aria-hidden="true"
        className="mt-1.5 h-2 w-2 rounded-full flex-shrink-0"
        style={{ backgroundColor: 'var(--color-text-muted, #6b7280)' }}
      />
      <span
        style={{ color: 'var(--color-text-primary)' }}
        data-testid="observation-sentence"
      >
        {deterministic_sentence}
      </span>
    </li>
  );
}

// ─── Phase group ──────────────────────────────────────────────────────────────

interface PhaseGroupProps {
  phaseName: string;
  observations: DeterministicObservation[];
}

function PhaseGroup({ phaseName, observations }: PhaseGroupProps) {
  const faults = sortFaults(observations.filter((o) => !o.is_positive && !o.is_plausibility_flagged));
  const positives = observations.filter((o) => o.is_positive && !o.is_plausibility_flagged);

  if (faults.length === 0 && positives.length === 0) return null;

  return (
    <div className="space-y-2">
      {/* Phase label */}
      <p
        className="text-xs font-semibold uppercase tracking-wider"
        style={{ color: 'var(--color-text-muted)' }}
      >
        {phaseName}
      </p>

      <ul className="space-y-2.5">
        {/* Faults first */}
        {faults.map((obs, idx) => (
          <ObservationRow key={`fault-${idx}`} observation={obs} />
        ))}
        {/* Positives after, with visual separation when both present */}
        {positives.length > 0 && faults.length > 0 && (
          <li aria-hidden="true" className="border-t border-border-subtle my-1" />
        )}
        {positives.map((obs, idx) => (
          <ObservationRow key={`positive-${idx}`} observation={obs} />
        ))}
      </ul>
    </div>
  );
}

// ─── Plausibility disclosure ──────────────────────────────────────────────────

interface PlausibilityDisclosureProps {
  items: DeterministicObservation[];
}

function PlausibilityDisclosure({ items }: PlausibilityDisclosureProps) {
  const [expanded, setExpanded] = useState(false);

  if (items.length === 0) return null;

  return (
    <div
      className="rounded-lg border px-3 py-2"
      style={{
        borderColor: 'var(--color-border-subtle)',
        backgroundColor: 'transparent',
      }}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center gap-2 text-xs"
        style={{ color: 'var(--color-text-muted)' }}
        aria-expanded={expanded}
        data-testid="plausibility-toggle"
      >
        <ChevronDownIcon
          className={`h-3.5 w-3.5 flex-shrink-0 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
        <span>
          Tracking uncertainty ({items.length}{' '}
          {items.length === 1 ? 'note' : 'notes'}) — not a coaching fault
        </span>
      </button>

      {expanded && (
        <ul className="mt-2 space-y-2 pl-5" data-testid="plausibility-list">
          {items.map((obs, idx) => (
            <li key={idx} className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
              {obs.deterministic_sentence}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─── Public component ─────────────────────────────────────────────────────────

export interface MovementAnalysisProps {
  /** Array from `metadata.deterministic_observations`. Renders nothing when absent or empty. */
  observations: DeterministicObservation[] | null | undefined;
  /**
   * Pass true when `llm_explanation` is absent so the section shows a fallback note
   * explaining the AI narrative is unavailable.
   */
  noLlmFallback?: boolean;
}

/**
 * MovementAnalysis renders deterministic trace observations on the evaluation
 * results page. It replaces (or supplements) the AI coaching narrative when
 * LLM coaching is unavailable.
 *
 * Returns null when `observations` is absent or empty — callers should not
 * render a section wrapper in that case.
 *
 * NOTE: internal numeric fields (sigma, raw_dev, etc.) are intentionally not
 * rendered here — the `deterministic_sentence` from the backend already embeds
 * any numbers the user should see.
 */
function MovementAnalysis({ observations, noLlmFallback = false }: MovementAnalysisProps) {
  if (!observations || observations.length === 0) return null;

  // Separate plausibility-flagged items from the main list.
  const mainObs = observations.filter((o) => !o.is_plausibility_flagged);
  const plausibilityItems = observations.filter((o) => o.is_plausibility_flagged);

  // Group observations by phase_name, preserving insertion order.
  const phaseMap = new Map<string, DeterministicObservation[]>();
  for (const obs of mainObs) {
    const group = phaseMap.get(obs.phase_name) ?? [];
    group.push(obs);
    phaseMap.set(obs.phase_name, group);
  }

  return (
    <section
      aria-label="Movement analysis"
      data-testid="movement-analysis-section"
      className="space-y-5"
    >
      {/* Section header with optional no-LLM fallback note */}
      <div className="flex items-start gap-2.5">
        <FingerPrintIcon
          className="h-5 w-5 flex-shrink-0 mt-0.5"
          style={{ color: 'var(--color-accent-cyan, #22d3ee)' }}
          aria-hidden="true"
        />
        <div>
          <h3
            className="text-sm font-semibold"
            style={{ color: 'var(--color-text-primary)' }}
          >
            Movement analysis
          </h3>
          {noLlmFallback ? (
            <p
              className="text-xs mt-0.5"
              style={{ color: 'var(--color-text-muted)' }}
              data-testid="no-llm-fallback-note"
            >
              AI coaching narrative unavailable — showing measured movement analysis.
            </p>
          ) : (
            <p
              className="text-xs mt-0.5"
              style={{ color: 'var(--color-text-muted)' }}
            >
              Deterministic measurements from pose analysis
            </p>
          )}
        </div>
      </div>

      {/* Phase groups */}
      {phaseMap.size > 0 && (
        <div className="space-y-4">
          {Array.from(phaseMap.entries()).map(([phaseName, phaseObs]) => (
            <PhaseGroup key={phaseName} phaseName={phaseName} observations={phaseObs} />
          ))}
        </div>
      )}

      {/* Plausibility-flagged items — hidden by default, user can expand */}
      {plausibilityItems.length > 0 && (
        <PlausibilityDisclosure items={plausibilityItems} />
      )}
    </section>
  );
}

export default MovementAnalysis;
